<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="origemNome"
        v-model="formData.origem_nome"
        :label="t('Nome da Origem')"
        :placeholder="t('Digite o nome da origem')"
        type="text"
        required
        :validation="validateOrigemNome"
        fluid
        data-testid="origens-nome-input"
      />
      <small v-if="errors.origem_nome" class="error-message" data-testid="origens-nome-error">{{ errors.origem_nome }}</small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import { useOrigens } from '../../composables/useOrigens';
import type { Origens_UpdateOrigemInput } from '../../contracts/Origens_UpdateOrigemInput';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type OrigemId = number | string | null;

interface OrigemRecord {
  origem_id?: number;
  origem_nome?: string;
  [key: string]: unknown;
}

interface ApiError extends Error {
  response?: {
    data?: {
      error?: string;
    };
  };
}

const props = withDefaults(defineProps<{
  origemId?: OrigemId;
  isEditMode?: boolean;
}>(), {
  origemId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { fetchOrigem, createOrigem, updateOrigem } = useOrigens();
import { permissionsState } from '../../services/permissionsService';

// Convert origemId to number for consistency
const origemIdNumber = computed<number | null>(() => {
  if (!props.origemId) return null;
  const num = typeof props.origemId === 'string' ? parseInt(props.origemId, 10) : props.origemId;
  log('OrigensForm', 'origemIdNumber', 'Converted ID', { original: props.origemId, converted: num });
  return num;
});

// Reactive data
const formData = reactive<{ origem_nome: string }>({
  origem_nome: ''
});

const errors = reactive<{ origem_nome: string }>({
  origem_nome: ''
});

const isLoading = ref<boolean>(false);

// Validation function for InputText component
const validateOrigemNome = (): boolean => {
  return !!formData.origem_nome && formData.origem_nome.trim() !== '' && formData.origem_nome.length <= 100;
};

const loadForEdit = async (): Promise<void> => {
  if (!origemIdNumber.value) {
    logWarn('OrigensForm', 'loadForEdit', 'No origemId provided');
    return;
  }

  log('OrigensForm', 'loadForEdit', 'Loading origin for edit', origemIdNumber.value);
  isLoading.value = true;

  try {
    const origem = await fetchOrigem(origemIdNumber.value) as unknown as OrigemRecord | null;
    log('OrigensForm', 'loadForEdit', 'Loaded origin data', origem);

    if (!origem) {
      throw new Error('Dados da origem não encontrados');
    }

    // Populate form
    formData.origem_nome = origem.origem_nome || '';

    // Clear errors
    clearErrors();
  } catch (err) {
    const error = err as ApiError;
    logError('OrigensForm', 'loadForEdit', 'Error loading origin', {
      origemId: origemIdNumber.value,
      error: error.message,
      stack: error.stack,
      response: error.response
    });

    const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar origem') + ': ' + errorMessage);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  log('OrigensForm', 'validateForm', 'Validating form');
  clearErrors();
  let isValid = true;

  if (!formData.origem_nome || formData.origem_nome.trim() === '') {
    errors.origem_nome = t('Campo obrigatório');
    isValid = false;
  } else if (formData.origem_nome.length > 100) {
    errors.origem_nome = t('Nome muito longo') + ' (max 100)';
    isValid = false;
  }

  log('OrigensForm', 'validateForm', 'Validation result', isValid);
  return isValid;
};

const clearErrors = (): void => {
  errors.origem_nome = '';
};

const resetForm = (): void => {
  log('OrigensForm', 'resetForm', 'Resetting form');
  formData.origem_nome = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  log('OrigensForm', 'handleSubmit', 'Form submitted');

  if (!validateForm()) {
    logWarn('OrigensForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const origemNome = formData.origem_nome.trim();

    if (props.isEditMode && origemIdNumber.value) {
      // Update existing origin — typed payload per Origens_UpdateOrigemInput
      const updatePayload: Origens_UpdateOrigemInput = {
        origem_id: origemIdNumber.value,
        origem_nome: origemNome
      };
      log('OrigensForm', 'handleSubmit', 'Updating origin', updatePayload);
      await updateOrigem(updatePayload);
      eventBus.emit('globalMessage.success', t('Origem atualizada com sucesso'));
    } else {
      // Create new origin — get franchise ID from session
      const franchiseIds = Object.keys(permissionsState.franquias);
      const franquiaId = franchiseIds.length > 0 ? Number(franchiseIds[0]) : 0;
      const createPayload = { origem_nome: origemNome, origem_franquia_id: franquiaId };
      log('OrigensForm', 'handleSubmit', 'Creating origin', createPayload);
      await createOrigem(createPayload);
      eventBus.emit('globalMessage.success', t('Origem criada com sucesso'));
    }

    emit('submit-success');
    resetForm();
  } catch (err) {
    const error = err as Error;
    logError('OrigensForm', 'handleSubmit', 'Error submitting form', error);
    eventBus.emit('globalMessage.error',
      props.isEditMode
        ? t('Erro ao atualizar origem') + ': ' + error.message
        : t('Erro ao criar origem') + ': ' + error.message
    );
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in origemId to reload data
watch(() => props.origemId, async (newId) => {
  log('OrigensForm', 'watch', 'origemId changed', { newId, isEditMode: props.isEditMode });
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

// Lifecycle
onMounted(() => {
  log('OrigensForm', 'mounted', 'Component mounted', { 
    origemId: props.origemId, 
    origemIdNumber: origemIdNumber.value, 
    isEditMode: props.isEditMode 
  });

  if (props.isEditMode && origemIdNumber.value) {
    loadForEdit();
  }
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
